import React, { useEffect, useRef, useState } from "react";
import { Badge, Button, Row, Col } from "react-bootstrap";
import receiptDefaultImage from "../../assets/empty-receipt-photo.jpg";
import realBookDefaultPhoto from "../../assets/empty-realbook-photo.jpg";

const ProofUploadDual = ({ onFilesUpload }) => {
    const [receiptPreview, setReceiptPreview] = useState(receiptDefaultImage);
    const [realPreview, setRealPreview] = useState(realBookDefaultPhoto);

    const [receiptFile, setReceiptFile] = useState(null);
    const [realFile, setRealFile] = useState(null);

    const [receiptStatus, setReceiptStatus] = useState(""); // "", "OK", "NO"
    const [realStatus, setRealStatus] = useState("");       // "", "OK", "NO"

    const [blinkOpacity, setBlinkOpacity] = useState(1);
    const blinkIntervalRef = useRef(null);

    const receiptInputRef = useRef(null);
    const realInputRef = useRef(null);
    const [proofs, setProofs] = useState({ receipt: null, real: null });


    useEffect(() => {
        const needBlink =
            (!receiptStatus || receiptStatus === "NO") ||
            (!realStatus || realStatus === "NO");

        if (needBlink) {
            blinkIntervalRef.current = setInterval(() => {
                setBlinkOpacity((p) => (p === 1 ? 0.5 : 1));
            }, 500);
        } else {
            clearInterval(blinkIntervalRef.current);
            setBlinkOpacity(1);
        }
        return () => clearInterval(blinkIntervalRef.current);
    }, [receiptStatus, realStatus]);

    const uploadToTemp = async (files) => {
        const formData = new FormData();
        files.forEach((f) => formData.append("photos", f));

        const res = await fetch(`${process.env.REACT_APP_API_URL}api/assignment/proof_temp`, {
            method: "POST",
            body: formData,
        });

        const data = await res.json();
        if (!res.ok) throw new Error(data?.message || "Upload failed");

        return {
            uploadedFiles: data.uploadedFiles || [],
            fileHashes: data.fileHashes || [],
            duplicates: data.duplicates || null,
        };
    };

    const handlePickReceipt = () => receiptInputRef.current?.click();
    const handlePickReal = () => realInputRef.current?.click();

    const handleChangeReceipt = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setReceiptFile(f);
        setReceiptPreview(URL.createObjectURL(f));

        try {
            // если вторая картинка уже выбрана — шлём обе одним запросом
            const filesToSend = [f, ...(realFile ? [realFile] : [])];
            const { uploadedFiles, fileHashes } = await uploadToTemp(filesToSend);

            setReceiptStatus("OK");

            // индекс для receipt всегда 0 (первый в списке)
            const nextProofs = {
                receipt: { fileName: uploadedFiles[0], hash: fileHashes[0] },
                // если real уже был загружен ранее — сохраняем его из локального стейта
                real: proofs.real,
            };

            setProofs(nextProofs);
            onFilesUpload?.(nextProofs);
        } catch {
            setReceiptStatus("NO");
        }
    };


    const handleChangeReal = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setRealFile(f);
        setRealPreview(URL.createObjectURL(f));

        try {
            const filesToSend = [ ...(receiptFile ? [receiptFile] : []), f ];
            const { uploadedFiles, fileHashes } = await uploadToTemp(filesToSend);

            setRealStatus("OK");

            // индекс real — последний в ответе
            const last = uploadedFiles.length - 1;

            const nextProofs = {
                // если receipt уже был загружен ранее — подтянем его из локального стейта
                receipt: proofs.receipt,
                real: { fileName: uploadedFiles[last], hash: fileHashes[last] },
            };

            setProofs(nextProofs);
            onFilesUpload?.(nextProofs);
        } catch {
            setRealStatus("NO");
        }
    };


    const BadgeByStatus = ({ status, waitingText, okText, errText }) => {
        if (!status) {
            return (
                <Badge
                    bg="secondary"
                    className="px-2 py-1"
                    style={{ fontSize: "0.875rem", fontWeight: "bold", opacity: blinkOpacity, transition: "opacity 0.5s" }}
                >
                    <i className="fa-solid fa-hourglass-half me-2 fa-spin" />
                    {waitingText}
                </Badge>
            );
        }
        if (status === "OK") {
            return (
                <Badge bg="success" className="px-2 py-1" style={{ fontSize: "0.875rem", fontWeight: "bold" }}>
                    <i className="fa-solid me-2 fa-check" />
                    {okText}
                </Badge>
            );
        }
        return (
            <Badge
                bg="danger"
                className="px-2 py-1"
                style={{ fontSize: "0.875rem", fontWeight: "bold", opacity: blinkOpacity, transition: "opacity 0.5s" }}
            >
                <i className="fa-solid me-2 fa-ban" />
                {errText}
            </Badge>
        );
    };

    return (
        <div className="w-100">
            <Row className="g-3">
                {/* LEFT: Receipt */}
                <Col xs={12} md={6}>
                    <div
                        className="d-flex flex-column align-items-center position-relative w-100 p-2"
                        style={{
                            minHeight: "auto",
                            gap: "0.75rem",
                        }}
                    >
                        <div
                            className="position-absolute"
                            style={{
                                top: 8,
                                right: 8,
                                zIndex: 1,
                            }}
                        >
                            <BadgeByStatus
                                status={receiptStatus}
                                waitingText="Waiting for Receipt..."
                                okText="Receipt: Uniqueness OK"
                                errText="Receipt: Duplicate Detected"
                            />
                        </div>

                        <img
                            src={receiptPreview}
                            alt="Receipt preview"
                            className="img-fluid border border-secondary rounded"
                            style={{
                                maxHeight: "280px",
                                width: "100%",
                                objectFit: "contain",
                                borderRadius: 4,
                            }}
                        />

                        <input
                            type="file"
                            accept="image/*"
                            ref={receiptInputRef}
                            className="d-none"
                            onChange={handleChangeReceipt}
                        />

                        <Button
                            variant="success"
                            className="mt-1 btn-sm"
                            onClick={handlePickReceipt}
                        >
                            {receiptStatus === "OK" ? "Re-Upload Receipt" : "Upload Receipt"}
                        </Button>
                    </div>
                </Col>

                {/* RIGHT: Real photo */}
                <Col xs={12} md={6}>
                    <div
                        className="d-flex flex-column align-items-center position-relative w-100 p-2"
                        style={{
                            minHeight: "auto",
                            gap: "0.75rem",
                        }}
                    >
                        <div
                            className="position-absolute"
                            style={{
                                top: 8,
                                right: 8,
                                zIndex: 1,
                            }}
                        >
                            <BadgeByStatus
                                status={realStatus}
                                waitingText="Waiting for Real Photo..."
                                okText="Real Photo: Uniqueness OK"
                                errText="Real Photo: Duplicate Detected"
                            />
                        </div>

                        <img
                            src={realPreview}
                            alt="Real book preview"
                            className="img-fluid border border-secondary rounded"
                            style={{
                                maxHeight: "280px",
                                width: "100%",
                                objectFit: "contain",
                                borderRadius: 4,
                            }}
                        />

                        <input
                            type="file"
                            accept="image/*"
                            ref={realInputRef}
                            className="d-none"
                            onChange={handleChangeReal}
                        />

                        <Button
                            variant="success"
                            className="mt-1 btn-sm"
                            onClick={handlePickReal}
                        >
                            {realStatus === "OK" ? "Re-Upload Real Photo" : "Upload Real Photo"}
                        </Button>
                    </div>
                </Col>

                {/* Info text */}
                <Col className="mt-2 text-muted">
                    Images you upload will be visible to the book’s author and one of our team members.
                    Please make sure your photos are clear and sharp, without any blur or distortion.
                    Do not include any sensitive information such as credit card numbers or personal details.
                    We only need a basic proof of purchase and a photo of the received book or item to confirm that you actually bought it.
                </Col>
            </Row>
        </div>

    );
};

export default ProofUploadDual;
